import {
	createContext,
	useContext,
	useState,
	useEffect,
	useReducer,
} from 'react';
import { getAllTalks, getFilters, getFilteredTalks } from '@utils/TalksUtils';
import Loader from '@components/Misc/Loader';

const MainContext = createContext({});

// Initial state
const initialState = {
	filters: {},
	selectedDay: [],
	selectedVenue: [],
	selectedCategories: [],
	selectedTags: [],
	allTalks: [],
	filteredTalks: [],
	scrollValue: 0,
	query: { query: [] },
	selectedTalkId: null,
	selectedCatsTags: [],
};

// Reducer
const reducer = (state, action) => {
	switch (action.type) {
		case 'SET_FILTERS':
			return {
				...state,
				filters: action.payload,
			};
		case 'SET_SELECTED_DAY':
			return {
				...state,
				selectedDay: action.payload,
			};
		case 'SET_SELECTED_VENUE':
			return {
				...state,
				selectedVenue: action.payload,
			};
		case 'SET_SELECTED_CAT_TAG_FILTERS':
			return {
				...state,
				selectedCatsTags: action.payload,
			};
		case 'SET_SELECTED_CATEGORIES':
			return {
				...state,
				selectedCategories: action.payload,
			};
		case 'SET_SELECTED_TAGS':
			return {
				...state,
				selectedTags: action.payload,
			};
		case 'SET_ALL_TALKS':
			return {
				...state,
				allTalks: action.payload,
			};
		case 'SET_FILTERED_TALKS':
			return {
				...state,
				filteredTalks: action.payload,
			};
		case 'SET_SCROLL_VALUE':
			return {
				...state,
				scrollValue: action.payload,
			};
		case 'SET_QUERY':
			return {
				...state,
				query: action.payload,
			};
		case 'SET_SELECTED_TALK_ID':
			return {
				...state,
				selectedTalkId: action.payload,
			};
		default:
			return state;
	}
};

const MainContextProvider = ({ children }) => {
	const [state, dispatch] = useReducer(reducer, initialState);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(null);
	const { filters, allTalks, query } = state;

	// Get default filters and all talks on very first load.
	useEffect(() => {
		getFilters(dispatch, setError);
		getAllTalks(dispatch, setLoading, setError);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	// Sets the default filters on first load
	useEffect(() => {
		if (filters.default) {
			dispatch({
				type: 'SET_SELECTED_DAY',
				payload: [parseInt(filters.default.talk_day)],
			});
			dispatch({
				type: 'SET_SELECTED_VENUE',
				payload: [parseInt(filters.default.talk_venue)],
			});
		}
	}, [filters.default]);

	// Render filtered talks with the updated filters query, from <Filters> comp.
	useEffect(() => {
		getFilteredTalks(dispatch, allTalks, query);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [query]);

	// Render filtered talks on first load, with the default filters.
	useEffect(() => {
		const params = {
			query: [],
		};

		if (filters?.default?.talk_day) {
			params.query.push({
				taxonomy: 'talk_day',
				term_id: filters.default.talk_day,
			});
		}

		if (filters?.default?.talk_venue) {
			params.query.push({
				taxonomy: 'talk_venue',
				term_id: filters.default.talk_venue,
			});
		}

		if (filters?.default?.talk_categories) {
			params.query.push({
				taxonomy: 'talk_category',
				term_id: filters.default.talk_categories,
			});
		}

		if (filters?.default?.talk_tags) {
			params.query.push({
				taxonomy: 'talk_tag',
				term_id: filters.default.talk_tags,
			});
		}

		getFilteredTalks(dispatch, allTalks, params);

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [allTalks]);

	return (
		<MainContext.Provider value={{ state, dispatch }}>
			{loading ? (
				<div className="cfs-talk-list-loader-wrapper">
					<Loader />
				</div>
			) : error ? (
				<p>Error: {error.message}</p>
			) : (
				children
			)}
		</MainContext.Provider>
	);
};

// Custom hook to get the context
const useMainContext = () => {
	const context = useContext(MainContext);
	if (!context) {
		throw new Error('useMainContext must be used within a TalksProvider');
	}
	return context;
};
export { useMainContext, MainContextProvider };
